import SuhaiGarageForm from '@components/organisms/forms/SuhaiGarageForm';
import SuhaiSchoolParkingForm from '@components/organisms/forms/SuhaiSchoolParkingForm';
import SuhaiUtilizationForm from '@components/organisms/forms/SuhaiUtilizationForm';
import SuhaiWorkParkingForm from '@components/organisms/forms/SuhaiWorkParkingForm';
import {
  PrefillQuoteWithViewerDataQuery,
  QuoteType,
  UserIntent,
  VehicleType,
  VehicleUtilization,
} from '@graphql/generated';
import { PARTNER_QUESTIONS, QUOTE_QUESTIONS } from '@lib/data/questions';
import { QuoteInitialForm } from '@lib/data/typesQuestions';
import { Question } from 'types/types';
import { partnerConfig } from './partner';

export function initialFormQuestions(
  quote?: QuoteInitialForm,
  data?: PrefillQuoteWithViewerDataQuery,
) {
  const filteredQuestions = QUOTE_QUESTIONS.filter((q) => {
    if (q.id === 'notInsured' && quote?.userIntent === UserIntent.SuhaiLead) {
      return false;
    }

    if (q.id === 'notInsured') {
      return quote?.type === QuoteType.New;
    }

    if (q.id === 'utilization') {
      return quote?.vehicle?.type !== VehicleType.Trucks;
    }

    if (q.id === 'houseType') {
      return quote?.vehicle?.type !== VehicleType.Trucks;
    }

    if (q.id === 'contact') {
      return !data?.viewer.email && !data?.viewer.phoneNumber;
    }

    if (q.id === 'licencePlate') {
      return !data?.viewer.vehicle.plate;
    }

    if (['carrier', 'bonus'].includes(q.id) && quote?.userIntent)
      return [UserIntent.Renovation, UserIntent.Expired].includes(
        quote?.userIntent,
      );

    if (q.id === 'workParking') {
      return (
        quote?.vehicle?.utilization?.includes(VehicleUtilization.Work) ||
        quote?.vehicle?.utilization?.includes(VehicleUtilization.Commuting)
      );
    }

    if (
      q.id === 'schoolParking' &&
      quote?.userIntent !== UserIntent.SuhaiPartner
    ) {
      return quote?.vehicle?.utilization?.includes(VehicleUtilization.School);
    }

    if (q.id === 'zeroKm') {
      return quote?.vehicle?.modelYear && quote?.vehicle?.modelYear >= 2021;
    }

    if (q.id === 'type') {
      return (
        !quote?.vehicleVersions?.some((v) => !!v.plate) &&
        !data?.viewer.vehicle.type
      );
    }

    if (q.id === 'manufacturingYear')
      return !quote?.vehicleVersions?.some((v) => !!v.manufacturingYear);

    if (['sex', 'birthday', 'name'].includes(q.id))
      return !(quote?.conductor as { id?: string })?.id;

    return true;
  });

  return filteredQuestions;
}

export function renewalFormQuestions(quote?: QuoteInitialForm) {
  return initialFormQuestions(quote).filter((q) =>
    [
      'licencePlate',
      'type',
      'vehicle',
      'manufacturingYear',
      'zeroKm',
      'cep',
      'utilization',
      'houseType',
      'houseParking',
      'workParking',
      'schoolParking',
      'cpf',
      'name',
      'birthday',
      'sex',
      'maritalStatus',
      'mainConductor',
      'youngCoverage',
      'contact',
    ].includes(q.id),
  );
}

export function partnersQuestion(quote?: QuoteInitialForm) {
  const contactQuestion = PARTNER_QUESTIONS.find((q) => q.id === 'contact');

  if (!contactQuestion) throw new Error('Contact question not found');

  const questions = partnerConfig?.config?.askForContactFirst
    ? [
        {
          ...contactQuestion,
          text: () => <span>Pra começar, quais os seus dados de contato?</span>,
        },
        ...PARTNER_QUESTIONS.filter((q) => q.id !== 'contact'),
      ]
    : PARTNER_QUESTIONS;

  const filteredQuestions = questions.filter((q) => {
    if (q.id === 'notInsured') {
      return quote?.type === QuoteType.New;
    }

    if (q.id === 'utilization') {
      return quote?.vehicle?.type !== VehicleType.Trucks;
    }

    if (q.id === 'houseType') {
      return quote?.vehicle?.type !== VehicleType.Trucks;
    }

    if (q.id === 'type') {
      return !quote?.vehicleVersions?.some((v) => !!v.plate);
    }

    if (['carrier', 'bonus'].includes(q.id) && quote?.userIntent)
      return [UserIntent.Renovation, UserIntent.Expired].includes(
        quote?.userIntent,
      );

    if (q.id === 'workParking') {
      return (
        quote?.vehicle?.utilization?.includes(VehicleUtilization.Work) ||
        quote?.vehicle?.utilization?.includes(VehicleUtilization.Commuting)
      );
    }

    if (
      q.id === 'schoolParking' &&
      quote?.userIntent !== UserIntent.SuhaiPartner
    ) {
      return quote?.vehicle?.utilization?.includes(VehicleUtilization.School);
    }

    if (q.id === 'zeroKm') {
      return quote?.vehicle?.modelYear && quote?.vehicle?.modelYear >= 2021;
    }

    if (q.id === 'manufacturingYear')
      return !quote?.vehicleVersions?.some((v) => !!v.manufacturingYear);

    if (['sex', 'birthday', 'name'].includes(q.id))
      return !(quote?.conductor as { id?: string })?.id;

    return true;
  });

  return filteredQuestions;
}

export function suhaiPartnerQuestions(quote?: QuoteInitialForm) {
  const questions = initialFormQuestions(quote).filter(
    (q) => !['youngCoverage', 'notInsured', 'houseType'].includes(q.id),
  );

  return questions
    .map((q) => {
      if (q.id === 'utilization') {
        return { ...q, Form: SuhaiUtilizationForm };
      }
      if (q.id === 'houseParking') {
        return {
          ...q,
          Form: SuhaiGarageForm,
          text: (d?: { vehicle?: { type?: VehicleType } }) =>
            d?.vehicle?.type === VehicleType.Trucks
              ? 'Mantém o veículo em garagem/estacionamento quando não está em serviço'
              : 'Veículo é guardado em garagem/estacionamento fechado na residência?',
        };
      }
      if (q.id === 'workParking') {
        return {
          ...q,
          Form: SuhaiWorkParkingForm,
          text: () =>
            'Veículo é guardado em garagem/estacionamento fechado quando utilizado para ir a local de trabalho?',
        };
      }

      if (q.id === 'schoolParking') {
        return {
          ...q,
          Form: SuhaiSchoolParkingForm,
          text: () =>
            'Veículo é guardado em garagem/estacionamento fechado quando utilizado para ir a faculdade/colégio?',
        };
      }
      return q;
    })
    .filter((q) => {
      if (q.id === 'schoolParking') {
        return quote?.vehicle?.utilization?.some((u) =>
          [
            VehicleUtilization.Work,
            VehicleUtilization.Commuting,
            VehicleUtilization.Leisure,
            VehicleUtilization.Delivery,
          ].includes(u),
        );
      }

      return true;
    });
}

export function getNextQuestionId({
  questions,
  actualId,
}: {
  questions: Question[];
  actualId: string;
}) {
  const index = questions.findIndex((q) => q.id === actualId);

  const newId = index >= 0 ? questions[index + 1]?.id : null;

  return newId;
}

export function getPreviousQuestionId({
  questions,
  actualId,
}: {
  questions: Question[];
  actualId: string;
}) {
  const index = questions.findIndex((q) => q.id === actualId);

  const newId = index >= 0 ? questions[index - 1]?.id : null;

  return newId;
}
